import { Box } from '@material-ui/core';
import {
  FeeType,
  formatMoney,
  formatPercent,
  getPricingById,
  patchRemovePricingSpecFromPricing,
  Pricing,
  PricingRange,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction, useState } from 'react';

import { LenderEarningFee } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/components/LenderEarningFee';
import { usePricingSpecsTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/hooks/usePricingSpecsTable';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface RemovePricingSpecFromPricingDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  pricing: Pricing;
  pricingRange: PricingRange;
}

export const RemovePricingSpecFromPricingDialog = ({
  opened,
  setOpened,
  toggleDialog,
  pricing,
  pricingRange,
}: RemovePricingSpecFromPricingDialogProps) => {
  const { request } = useRequest();
  const { palette } = useCustomTheme();
  const { classes } = usePricingSpecsTable(pricing);
  const { revalidate } = useCache(getPricingById, { pricingId: pricing.id }, { revalidateOnMount: false });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onSubmit = async (): Promise<void> => {
    if (!pricing) return;
    setIsSubmitting(true);
    try {
      request(patchRemovePricingSpecFromPricing, { pricingId: pricing.id, pricingSpecId: pricingRange.id });
      await revalidate();
    } catch (err) {
      return;
    } finally {
      setIsSubmitting(false);
    }
    toggleDialog();
  };

  return (
    <NemuruDialog
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton main onClick={onSubmit} loading={isSubmitting}>
            <Text tId="REMOVE_PRICING_SPEC_FROM_PRICING" />
          </StandardButton>
        </>
      }
    >
      <Box mb={5}>
        <Font variant="h3" color="title">
          <Text tId={'REMOVE_PRICING_SPEC_FROM_PRICING'} />
        </Font>
      </Box>
      <Box mb={4}>
        <Font variant="body2" color="textSecondary">
          <Text tId={'REMOVE_PRICING_SPEC_FROM_PRICING_DESCRIPTION'} />
        </Font>
      </Box>
      <Box>
        <TextFormField
          label={'PRINCIPAL'}
          value={`${formatMoney(
            { value: pricingRange.rangePrincipalMin, currency: pricing.currency },
            2,
          )} - ${formatMoney({ value: pricingRange.rangePrincipalMax, currency: pricing.currency }, 2)}`}
          name={'rangePrincipal'}
        />
        <TextFormField
          label={'TERM'}
          value={`${pricingRange.rangePeriodMin} - ${pricingRange.rangePeriodMax}`}
          name={'rangePeriod'}
        />
        <TextFormField
          label={'ANNUAL_INTEREST'}
          value={pricingRange.annualInterest !== null ? formatPercent(pricingRange.annualInterest) : ''}
          name={'annualInterest'}
        />
        <TextFormField
          label={'SETUP_FEE'}
          value={
            <>
              {pricingRange.setupFee.type === FeeType.FIX_AMOUNT
                ? formatMoney({ value: pricingRange.setupFee.value, currency: pricing.currency })
                : formatPercent(pricingRange.setupFee.value)}{' '}
              <Text tId={pricingRange.setupFee.paymentType} />
            </>
          }
          name={'setupFee'}
        />
        <TextFormField
          label={'COST_MERCHANT'}
          value={
            pricingRange.merchantDiscountPercentage !== null
              ? formatPercent(pricingRange.merchantDiscountPercentage, 2)
              : ''
          }
          name={'merchantDiscountPercentage'}
        />
        <TextFormField
          label={'LENDER_PRICING_NAME'}
          value={
            <>
              {pricingRange.lenderEarningsFees
                ?.sort((a, b) => (a.lenderId > b.lenderId ? 1 : -1))
                .map((lenderEarningFee) => {
                  return (
                    <LenderEarningFee
                      key={`${pricingRange.id}-${pricingRange.id}`}
                      pricingSpecInLenderContext={lenderEarningFee}
                      palette={palette}
                      classes={classes}
                    />
                  );
                })}
            </>
          }
          name={'lenderEarningsFees'}
        />
      </Box>
    </NemuruDialog>
  );
};
